import type { ReactNode } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import { Link } from 'react-router-dom';
import { cn } from '@/utils/cn';

export interface ComingSoonStateProps {
  title: string;
  description?: string;
  backTo?: string;
  backLabel?: string;
  icon?: ReactNode;
  className?: string;
}

export function ComingSoonState({
  title,
  description,
  backTo,
  backLabel = 'Back to settings',
  icon,
  className,
}: ComingSoonStateProps) {
  return (
    <div
      className={cn(
        'mx-auto flex w-full max-w-lg flex-col items-center rounded-2xl border border-slate-200/80 bg-white px-6 py-14 text-center shadow-sm',
        'dark:border-slate-800 dark:bg-slate-900/50',
        className,
      )}
    >
      <div className="relative mb-5">
        <div
          className={cn(
            'flex h-14 w-14 items-center justify-center rounded-2xl',
            'bg-gradient-to-br from-violet-50 via-white to-slate-50',
            'ring-1 ring-slate-200/90 dark:from-violet-950/50 dark:via-slate-900 dark:to-slate-950 dark:ring-slate-700',
          )}
          aria-hidden
        >
          {icon ?? (
            <ScheduleOutlinedIcon
              className="text-violet-600 dark:text-violet-400"
              sx={{ fontSize: 28 }}
            />
          )}
        </div>
        <span
          className={cn(
            'absolute -right-2 -top-2 rounded-full px-2 py-0.5',
            'bg-amber-100 text-[10px] font-semibold uppercase tracking-wider text-amber-800',
            'dark:bg-amber-950/80 dark:text-amber-200',
          )}
        >
          Soon
        </span>
      </div>

      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400">
        Coming soon
      </p>
      <h2 className="mt-2 text-xl font-semibold tracking-tight text-slate-900 dark:text-white">
        {title}
      </h2>
      {description ? (
        <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-600 dark:text-slate-400">
          {description}
        </p>
      ) : (
        <p className="mt-3 max-w-sm text-sm leading-relaxed text-slate-600 dark:text-slate-400">
          This screen is being prepared and will be available in an upcoming release.
        </p>
      )}

      <div className="mt-6 flex items-center gap-1.5" aria-hidden>
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={cn(
              'h-1.5 rounded-full bg-violet-200 dark:bg-violet-800/80',
              i === 1 ? 'w-6' : 'w-1.5',
            )}
          />
        ))}
      </div>

      {backTo ? (
        <Link
          to={backTo}
          className={cn(
            'mt-8 inline-flex items-center gap-1.5 text-sm font-medium',
            'text-violet-600 transition-colors hover:text-violet-700',
            'dark:text-violet-400 dark:hover:text-violet-300',
          )}
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} />
          {backLabel}
        </Link>
      ) : null}
    </div>
  );
}
